import React, { useState, useEffect, useCallback } from 'react';
import { getLkUsers, addLkUser, updateLkUser, deleteLkUser } from '../api';
import './LkUsersPage.css';

function LkUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Форма добавления
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newDeviceUid, setNewDeviceUid] = useState('');
  const [adding, setAdding] = useState(false);

  // Редактирование
  const [editingUser, setEditingUser] = useState(null); // username
  const [editPassword, setEditPassword] = useState('');
  const [editDeviceUid, setEditDeviceUid] = useState('');
  const [saving, setSaving] = useState(false);

  // Загрузка списка
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getLkUsers();
      setUsers(result.users || []);
    } catch (err) {
      setError('Ошибка загрузки: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Добавление пользователя
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newUsername || !newPassword || !newDeviceUid) return;

    setAdding(true);
    setError('');
    setSuccess('');
    try {
      await addLkUser(newUsername, newPassword, newDeviceUid);
      setSuccess(`Пользователь «${newUsername}» добавлен`);
      setNewUsername('');
      setNewPassword('');
      setNewDeviceUid('');
      await fetchUsers();
    } catch (err) {
      setError('Ошибка добавления: ' + (err.message || 'Unknown error'));
    } finally {
      setAdding(false);
    }
  };

  // Удаление
  const handleDelete = async (username) => {
    if (!window.confirm(`Удалить пользователя «${username}»?`)) return;

    setError('');
    setSuccess('');
    try {
      await deleteLkUser(username);
      setSuccess(`Пользователь «${username}» удалён`);
      if (editingUser === username) setEditingUser(null);
      await fetchUsers();
    } catch (err) {
      setError('Ошибка удаления: ' + (err.message || 'Unknown error'));
    }
  };

  // Начало редактирования
  const startEdit = (user) => {
    setEditingUser(user.username);
    setEditPassword('');
    setEditDeviceUid(user.device_uid);
    setSuccess('');
    setError('');
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditPassword('');
    setEditDeviceUid('');
  };

  // Сохранение изменений
  const handleSave = async () => {
    const data = {};
    if (editPassword) data.password = editPassword;

    // Проверяем, изменился ли device_uid
    const original = users.find((u) => u.username === editingUser);
    if (editDeviceUid && editDeviceUid !== (original ? original.device_uid : '')) {
      data.device_uid = editDeviceUid;
    }

    if (Object.keys(data).length === 0) {
      cancelEdit();
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateLkUser(editingUser, data);
      setSuccess(`Пользователь «${editingUser}» обновлён`);
      setEditingUser(null);
      setEditPassword('');
      setEditDeviceUid('');
      await fetchUsers();
    } catch (err) {
      setError('Ошибка обновления: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="lk-users-page">
      <div className="page-header">
        <h1>👤 Личные кабинеты</h1>
        <div className="page-header-actions">
          <button className="secondary" onClick={fetchUsers} disabled={loading}>
            Обновить
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Форма добавления */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <form className="add-form" onSubmit={handleAdd}>
          <div className="form-group">
            <label>Имя пользователя</label>
            <input
              type="text"
              placeholder="username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              disabled={adding}
            />
          </div>
          <div className="form-group">
            <label>Пароль</label>
            <input
              type="password"
              placeholder="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={adding}
            />
          </div>
          <div className="form-group">
            <label>Device UID</label>
            <input
              type="text"
              placeholder="piano_001"
              value={newDeviceUid}
              onChange={(e) => setNewDeviceUid(e.target.value)}
              disabled={adding}
            />
          </div>
          <button type="submit" className="primary" disabled={adding || !newUsername || !newPassword || !newDeviceUid}>
            {adding ? 'Добавление...' : 'Добавить'}
          </button>
        </form>
      </div>

      {/* Таблица */}
      {loading ? (
        <div className="loading">Загрузка пользователей</div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👤</div>
          <p>Нет пользователей ЛК</p>
        </div>
      ) : (
        <div className="card">
          <table className="users-table">
            <thead>
              <tr>
                <th>Имя пользователя</th>
                <th>Device UID</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.username}>
                  <td>{user.username}</td>
                  <td>
                    {editingUser === user.username ? (
                      <input
                        className="edit-input"
                        type="text"
                        value={editDeviceUid}
                        onChange={(e) => setEditDeviceUid(e.target.value)}
                        disabled={saving}
                      />
                    ) : (
                      user.device_uid
                    )}
                  </td>
                  <td>
                    {editingUser === user.username ? (
                      <div className="row-actions">
                        <input
                          className="edit-input"
                          type="password"
                          placeholder="новый пароль"
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          disabled={saving}
                        />
                        <button className="save" onClick={handleSave} disabled={saving}>
                          {saving ? '...' : '✓'}
                        </button>
                        <button onClick={cancelEdit} disabled={saving}>✕</button>
                      </div>
                    ) : (
                      <div className="row-actions">
                        <button onClick={() => startEdit(user)}>✏️ Изменить</button>
                        <button className="danger" onClick={() => handleDelete(user.username)}>🗑 Удалить</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LkUsersPage;